import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChevronDown, ArrowUpDown, Loader as LoaderIcon, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loader from '../../shared/components/ui/Loader';
import FormInput from '../../shared/components/ui/FormInput';
import PaginationComponent from '../../shared/components/PaginationComponent';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import FileDownload from '../../shared/assets/svg/download.svg';
import { getInventoryReportDetails } from '../services/api/implementation/impl';
import { groupRowsByBlockAndLevel } from '../utils/floorGrouping';
import { FloorTableGroupHeader } from '../components/ui/FloorCollapsibleSection';

const alphaSort = (a, b) =>
    String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
    });

const floorLabelPrefix = (name) =>
    /^(floor|level|ground)/i.test(String(name || '').trim()) ? '' : 'Floor ';

const blockHasNamedFloors = (blockGroup) =>
    (blockGroup?.levels || []).some((level) => level.name !== 'Unassigned');

// Statuses that count as "sold" for customer, booking date, sold price, and pending payments
const SOLD_STATUSES = [
    'Booked',
    'Sold',
    'Sales_Deed_Completed',
    'No_Dues',
    'Sales_Deed_with_Pending_Payments'
];

// Same formula as Sales Unit Inventory "Final Price":
// cost + PLC + GST + extra charges
const getUnitListPrice = (item) => {
    const cost = Number(item?.cost) || 0;
    const plc = Math.ceil(Number(item?.plc_price) || 0);
    const gst = Number(item?.gst) || 0;
    const extra = Number(item?.extra_charges) || 0;
    return cost + plc + gst + extra;
};

const processItem = (item) => {
    const closedDeal = item?.closedDeal?.[0];
    const isSold = SOLD_STATUSES.includes(item?.status);
    const listPrice = getUnitListPrice(item);
    const blockName = item?.block?.name || '';
    const floorName = item?.level?.name || '';
    return {
        id: item?.id,
        block_name: blockName || 'N/A',
        floor_name: floorName,
        unit_name: item?.unitName || 'N/A',
        status: item?.status || 'N/A',
        customer_name: isSold ? (closedDeal?.lead?.user?.name || 'N/A') : '-',
        booking_date: isSold && closedDeal?.closed_at ? moment(closedDeal.closed_at).format('DD-MM-YYYY') : '-',
        plot_area: item?.accommodation?.plotArea || 'N/A',
        build_area: item?.accommodation?.buildUpArea || 'N/A',
        actual_price: listPrice,
        sold_price: isSold ? (closedDeal?.total_price != null && closedDeal?.total_price !== '' ? (parseFloat(closedDeal.total_price) || 0) : 'N/A') : '-',
        unsold_price: !isSold ? listPrice : '-',
        pending_payments: isSold ? (closedDeal?.due_price != null && closedDeal?.due_price !== '' ? (parseFloat(closedDeal.due_price) || 0) : 'N/A') : '-'
    };
};

const SalesInventoryDetails = () => {
    const { register } = useForm({
        defaultValues: {
            accommodationType: 'Independent'
        }
    });

    const [accommodationType, setAccommodationType] = useState('Independent');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    // Status Filter State
    const [statusFilter, setStatusFilter] = useState('');
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

    // Date Sort State
    const [dateSort, setDateSort] = useState(''); // 'Closest' | 'Furthest' | ''
    const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

    // Pending Payments Sort State
    const [pendingSort, setPendingSort] = useState(''); // 'Low to High' | 'High to Low' | ''
    const [isPendingDropdownOpen, setIsPendingDropdownOpen] = useState(false);

    // Project Filter State
    const [projectFilter, setProjectFilter] = useState('');
    const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);

    // Block Filter State
    const [blockFilter, setBlockFilter] = useState('');
    const [isBlockDropdownOpen, setIsBlockDropdownOpen] = useState(false);
    const [isBlockHeaderDropdownOpen, setIsBlockHeaderDropdownOpen] = useState(false);

    const [collapsedBlocks, setCollapsedBlocks] = useState(() => new Set());
    const [collapsedLevels, setCollapsedLevels] = useState(() => new Set());

    // Excel Download State
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

    const accommodationOptions = [
        { label: 'Independent', value: 'Independent' },
        { label: 'Group', value: 'Group' }
    ];

    const { data: reportResponse, isFetching } = useQuery({
        queryKey: ['inventoryReportDetails', page, limit, accommodationType],
        queryFn: () => getInventoryReportDetails(page, limit, accommodationType),
        keepPreviousData: true
    });

    const reportData = reportResponse?.data || [];
    const totalItems = reportResponse?.meta?.total || reportData.length || 0;
    const totalPages = Math.ceil(totalItems / limit);

    const matchesScope = useCallback((item) => {
        if (accommodationType && item?.accommodation?.accomodationType?.toLowerCase() !== accommodationType.toLowerCase()) {
            return false;
        }
        if (projectFilter && item?.project?.name !== projectFilter) {
            return false;
        }
        if (blockFilter && item?.block?.name !== blockFilter) {
            return false;
        }
        return true;
    }, [accommodationType, projectFilter, blockFilter]);

    // Derive available projects from data
    const availableProjects = useMemo(() => {
        const projects = reportData
            .map(item => item?.project?.name)
            .filter(Boolean);
        return [...new Set(projects)].sort(alphaSort);
    }, [reportData]);

    const availableBlocks = useMemo(() => {
        const blocks = reportData
            .filter((item) => {
                if (accommodationType && item?.accommodation?.accomodationType?.toLowerCase() !== accommodationType.toLowerCase()) {
                    return false;
                }
                if (projectFilter && item?.project?.name !== projectFilter) {
                    return false;
                }
                return Boolean(item?.block?.name);
            })
            .map((item) => item.block.name);
        return [...new Set(blocks)].sort(alphaSort);
    }, [reportData, accommodationType, projectFilter]);

    // Client-side filtering and sorting
    const processedAndFilteredData = useMemo(() => {
        return reportData
            .filter(item => {
                if (statusFilter && item?.status?.toLowerCase() !== statusFilter.toLowerCase()) {
                    return false;
                }
                return matchesScope(item);
            })
            .sort((a, b) => {
                if (dateSort) {
                    const dateA = a?.closedDeal?.[0]?.closed_at;
                    const dateB = b?.closedDeal?.[0]?.closed_at;

                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;

                    const momentA = moment(dateA);
                    const momentB = moment(dateB);

                    if (dateSort === 'Closest') {
                        return momentB.diff(momentA);
                    }
                    if (dateSort === 'Furthest') {
                        return momentA.diff(momentB);
                    }
                }

                if (pendingSort) {
                    const pendingA = parseFloat(a?.closedDeal?.[0]?.due_price) || 0;
                    const pendingB = parseFloat(b?.closedDeal?.[0]?.due_price) || 0;

                    if (pendingSort === 'Low to High') {
                        return pendingA - pendingB;
                    }
                    if (pendingSort === 'High to Low') {
                        return pendingB - pendingA;
                    }
                }

                const blockCmp = alphaSort(a?.block?.name, b?.block?.name);
                if (blockCmp !== 0) return blockCmp;
                const levelCmp = alphaSort(a?.level?.name, b?.level?.name);
                if (levelCmp !== 0) return levelCmp;
                return alphaSort(a?.unitName, b?.unitName);
            });
    }, [reportData, statusFilter, matchesScope, dateSort, pendingSort]);

    const processedRows = useMemo(() => {
        return processedAndFilteredData.map((rawItem) => {
            const item = processItem(rawItem);
            return {
                ...item,
                block: item.block_name !== 'N/A' ? item.block_name : '',
                level: item.floor_name || (accommodationType === 'Independent' ? 'Unassigned' : ''),
                unit: item.unit_name
            };
        });
    }, [processedAndFilteredData, accommodationType]);

    const inventoryGroups = useMemo(
        () => groupRowsByBlockAndLevel(processedRows),
        [processedRows]
    );

    const columnCount = accommodationType === 'Group' ? 10 : 11;

    useEffect(() => {
        setCollapsedBlocks(new Set());
        setCollapsedLevels(new Set());
    }, [accommodationType, projectFilter, blockFilter, statusFilter]);

    const toggleBlockCollapse = useCallback((blockKey) => {
        setCollapsedBlocks((prev) => {
            const next = new Set(prev);
            if (next.has(blockKey)) next.delete(blockKey);
            else next.add(blockKey);
            return next;
        });
    }, []);

    const toggleLevelCollapse = useCallback((levelKey) => {
        setCollapsedLevels((prev) => {
            const next = new Set(prev);
            if (next.has(levelKey)) next.delete(levelKey);
            else next.add(levelKey);
            return next;
        });
    }, []);

    const closeAllDropdowns = () => {
        setIsStatusDropdownOpen(false);
        setIsDateDropdownOpen(false);
        setIsPendingDropdownOpen(false);
        setIsProjectDropdownOpen(false);
        setIsBlockDropdownOpen(false);
        setIsBlockHeaderDropdownOpen(false);
    };

    const statusOptions = [
        'Sold',
        'Available',
        'Booked',
        'Vacant',
        'Blocked',
        'Temporary_Booked',
        'Sales_Deed_Completed',
        'Sales_Deed_with_Pending_Payments',
        'No_Dues',
        'Blocked_for_Broker',
        'Booked_Incomplete',
        'Temporary_Booked_Incomplete'
    ];
    const dateSortOptions = ['Closest', 'Furthest'];
    const pendingSortOptions = ['Low to High', 'High to Low'];

    // Calculate total sold and unsold prices filtered by accommodation type, project, and block
    const scopedData = reportData.filter(matchesScope);

    const totalSoldPrice = scopedData
        .reduce((sum, rawItem) => {
            const isSold = SOLD_STATUSES.includes(rawItem?.status);
            if (isSold) {
                const closedDeal = rawItem?.closedDeal?.[0];
                const soldPrice = parseFloat(closedDeal?.total_price) || 0;
                return sum + soldPrice;
            }
            return sum;
        }, 0);

    const totalUnsoldPrice = scopedData
        .reduce((sum, rawItem) => {
            const isSold = SOLD_STATUSES.includes(rawItem?.status);
            if (!isSold) {
                return sum + getUnitListPrice(rawItem);
            }
            return sum;
        }, 0);

    const filteredForAreas = scopedData;

    const totalSoldPlotArea = filteredForAreas.reduce((sum, rawItem) => {
        if (!SOLD_STATUSES.includes(rawItem?.status)) return sum;
        const area = parseFloat(rawItem?.accommodation?.plotArea);
        return sum + (Number.isFinite(area) ? area : 0);
    }, 0);

    const totalUnsoldPlotArea = filteredForAreas.reduce((sum, rawItem) => {
        if (SOLD_STATUSES.includes(rawItem?.status)) return sum;
        const area = parseFloat(rawItem?.accommodation?.plotArea);
        return sum + (Number.isFinite(area) ? area : 0);
    }, 0);

    const totalSoldBuildUpArea = filteredForAreas.reduce((sum, rawItem) => {
        if (!SOLD_STATUSES.includes(rawItem?.status)) return sum;
        const area = parseFloat(rawItem?.accommodation?.buildUpArea);
        return sum + (Number.isFinite(area) ? area : 0);
    }, 0);

    const totalUnsoldBuildUpArea = filteredForAreas.reduce((sum, rawItem) => {
        if (SOLD_STATUSES.includes(rawItem?.status)) return sum;
        const area = parseFloat(rawItem?.accommodation?.buildUpArea);
        return sum + (Number.isFinite(area) ? area : 0);
    }, 0);

    // Format area for display (e.g. "1,234.56")
    const formatArea = (value) => {
        if (value == null || !Number.isFinite(value) || value === 0) return '0';
        return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Format currency with compact Indian units based on magnitude
    const formatCurrencyByMagnitude = (amount) => {
        const num = Number(amount) || 0;
        const abs = Math.abs(num);
        const sign = num < 0 ? '-' : '';

        if (abs >= 10000000) {
            return `${sign}₹${(abs / 10000000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr`;
        }
        if (abs >= 100000) {
            return `${sign}₹${(abs / 100000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Lakh`;
        }
        if (abs >= 1000) {
            return `${sign}₹${(abs / 1000).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Thousand`;
        }
        if (abs >= 100) {
            return `${sign}₹${(abs / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Hundred`;
        }
        return `${sign}₹${abs.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Format price for table cells (up to 2 decimals, same as PLC formatAmount)
    const formatPrice = (value) => {
        if (value === 'N/A' || value === '-') return value;
        const num = parseFloat(value);
        if (isNaN(num)) return '0.00';
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Handler for downloading Excel
    const handleDownloadExcel = async () => {
        if (isDownloadingExcel) return;

        try {
            setIsDownloadingExcel(true);
            toast.loading('Preparing Excel file...', { id: 'excel-download' });

            if (!processedAndFilteredData || processedAndFilteredData.length === 0) {
                toast.error('No data available to download', { id: 'excel-download' });
                setIsDownloadingExcel(false);
                return;
            }

            // Create workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Inventory Details');

            // Define columns based on accommodation type
            const columns = [
                { header: 'S.No', key: 's_no', width: 8 },
                { header: 'Block Name', key: 'block_name', width: 20 },
                { header: 'Floor', key: 'floor_name', width: 14 },
                { header: 'Unit Name', key: 'unit_name', width: 20 },
                { header: 'Status', key: 'status', width: 15 },
                { header: 'Customer Name', key: 'customer_name', width: 25 },
                { header: 'Booking Date', key: 'booking_date', width: 15 }
            ];

            // Add Plot Area column only for Independent type
            if (accommodationType !== 'Group') {
                columns.push({ header: 'Plot Area', key: 'plot_area', width: 12 });
            }

            columns.push(
                { header: 'Build Area', key: 'build_area', width: 12 },
                { header: 'Actual Price', key: 'actual_price', width: 15 },
                { header: 'Sold Price', key: 'sold_price', width: 15 },
                { header: 'Unsold Price', key: 'unsold_price', width: 15 },
                { header: 'Pending Payments', key: 'pending_payments', width: 18 }
            );

            worksheet.columns = columns;

            const exportRows = inventoryGroups.flatMap((blockGroup) =>
                blockGroup.levels.flatMap((levelGroup) =>
                    levelGroup.rows.map((item) => ({
                        ...item,
                        block_name: blockGroup.name,
                        floor_name: levelGroup.name
                    }))
                )
            );

            exportRows.forEach((item, index) => {
                const rowData = {
                    s_no: index + 1,
                    block_name: item.block_name,
                    floor_name: item.floor_name,
                    unit_name: item.unit_name,
                    status: item.status,
                    customer_name: item.customer_name,
                    booking_date: item.booking_date
                };

                if (accommodationType !== 'Group') {
                    rowData.plot_area = item.plot_area;
                }

                rowData.build_area = item.build_area;
                rowData.actual_price = typeof item.actual_price === 'number' ? formatPrice(item.actual_price) : item.actual_price;
                rowData.sold_price = typeof item.sold_price === 'number' ? formatPrice(item.sold_price) : item.sold_price;
                rowData.unsold_price = typeof item.unsold_price === 'number' ? formatPrice(item.unsold_price) : item.unsold_price;
                rowData.pending_payments = typeof item.pending_payments === 'number' ? formatPrice(item.pending_payments) : item.pending_payments;

                worksheet.addRow(rowData);
            });

            // Style header row
            worksheet.getRow(1).font = { bold: true };

            // Generate buffer and save
            const buffer = await workbook.xlsx.writeBuffer();
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const projectSuffix = projectFilter ? `_${projectFilter.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
            const blockSuffix = blockFilter ? `_${blockFilter.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, `Inventory_Details_${accommodationType}${projectSuffix}${blockSuffix}_${timestamp}.xlsx`);

            toast.success(`Downloaded ${processedAndFilteredData.length} records successfully!`, {
                id: 'excel-download'
            });
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error(error?.message || 'Failed to download Excel file', {
                id: 'excel-download'
            });
        } finally {
            setIsDownloadingExcel(false);
        }
    };

    const renderUnitRow = (item, index) => (
        <tr key={item.id || index} className="hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3 whitespace-nowrap pl-10">{item.block_name}</td>
            <td className="px-4 py-3 whitespace-nowrap">{item.unit_name}</td>
            <td className="px-4 py-3 whitespace-nowrap">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${SOLD_STATUSES.includes(item.status)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {item.status}
                </span>
            </td>
            <td className="px-4 py-3 whitespace-nowrap">{item.customer_name}</td>
            <td className="px-4 py-3 whitespace-nowrap">{item.booking_date}</td>
            {accommodationType !== 'Group' && <td className="px-4 py-3 whitespace-nowrap">{item.plot_area}</td>}
            <td className="px-4 py-3 whitespace-nowrap">{item.build_area}</td>
            <td className="px-4 py-3 whitespace-nowrap">{typeof item.actual_price === 'number' ? `₹ ${formatPrice(item.actual_price)}` : item.actual_price}</td>
            <td className="px-4 py-3 whitespace-nowrap">{typeof item.sold_price === 'number' ? `₹ ${formatPrice(item.sold_price)}` : item.sold_price}</td>
            <td className="px-4 py-3 whitespace-nowrap">{typeof item.unsold_price === 'number' ? `₹ ${formatPrice(item.unsold_price)}` : item.unsold_price}</td>
            <td className="px-4 py-3 whitespace-nowrap">{typeof item.pending_payments === 'number' ? `₹ ${formatPrice(item.pending_payments)}` : item.pending_payments}</td>
        </tr>
    );

    return (
        <div className="w-full flex flex-col z-5 bg-gray-100 overflow-hidden" onClick={closeAllDropdowns}>
            {/* Search & Filter/Download Icons */}
            <div className="flex items-center justify-between px-6 py-4 bg-white border-t-[2px] border-stone-100 shadow-sm gap-4">
                {/* Dropdown for Accommodation Type */}
                <div className="flex items-center gap-4">
                    <div>
                        <p className="font-semibold text-gray-700">Accommodation Type</p>
                        <div className="w-[200px]">
                            <FormInput
                                width={100}
                                register={register}
                                register_key="accommodationType"
                                type="dropdown"
                                options={accommodationOptions}
                                onDropdownChange={(val) => {
                                    setAccommodationType(val);
                                    setPage(1);
                                    setStatusFilter('');
                                    setDateSort('');
                                    setPendingSort('');
                                    setProjectFilter('');
                                    setBlockFilter('');
                                }}
                                isRequired={false}
                            />
                        </div>
                    </div>

                    {/* Project Filter Dropdown */}
                    <div>
                        <p className="font-semibold text-gray-700">Project</p>
                        <div className="relative w-[200px]">
                            <div
                                className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer bg-white hover:border-gray-400 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsProjectDropdownOpen(!isProjectDropdownOpen);
                                    setIsStatusDropdownOpen(false);
                                    setIsDateDropdownOpen(false);
                                    setIsPendingDropdownOpen(false);
                                    setIsBlockDropdownOpen(false);
                                    setIsBlockHeaderDropdownOpen(false);
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className={projectFilter ? "text-gray-900" : "text-gray-500"}>
                                        {projectFilter || 'All Projects'}
                                    </span>
                                    <ChevronDown size={16} className="text-gray-500" />
                                </div>
                            </div>
                            {isProjectDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-50 max-h-60 overflow-y-auto">
                                    <div
                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${projectFilter === '' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                        onClick={() => {
                                            setProjectFilter('');
                                            setBlockFilter('');
                                            setIsProjectDropdownOpen(false);
                                        }}
                                    >
                                        All Projects
                                    </div>
                                    {availableProjects.map(project => (
                                        <div
                                            key={project}
                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${projectFilter === project ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                            onClick={() => {
                                                setProjectFilter(project);
                                                setBlockFilter('');
                                                setIsProjectDropdownOpen(false);
                                            }}
                                        >
                                            {project}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Block Filter Dropdown */}
                    <div>
                        <p className="font-semibold text-gray-700">Block</p>
                        <div className="relative w-[200px]">
                            <div
                                className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer bg-white hover:border-gray-400 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsBlockDropdownOpen(!isBlockDropdownOpen);
                                    setIsProjectDropdownOpen(false);
                                    setIsStatusDropdownOpen(false);
                                    setIsDateDropdownOpen(false);
                                    setIsPendingDropdownOpen(false);
                                    setIsBlockHeaderDropdownOpen(false);
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <span className={blockFilter ? "text-gray-900" : "text-gray-500"}>
                                        {blockFilter || 'All Blocks'}
                                    </span>
                                    <ChevronDown size={16} className="text-gray-500" />
                                </div>
                            </div>
                            {isBlockDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-50 max-h-60 overflow-y-auto">
                                    <div
                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${blockFilter === '' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                        onClick={() => {
                                            setBlockFilter('');
                                            setIsBlockDropdownOpen(false);
                                        }}
                                    >
                                        All Blocks
                                    </div>
                                    {availableBlocks.map(block => (
                                        <div
                                            key={block}
                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${blockFilter === block ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                            onClick={() => {
                                                setBlockFilter(block);
                                                setIsBlockDropdownOpen(false);
                                            }}
                                        >
                                            {block}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(statusFilter || dateSort || pendingSort || projectFilter || blockFilter) && (
                        <div className="flex items-center gap-2 flex-wrap">

                            {statusFilter && (
                                <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                                    <span>Status: {statusFilter.replace('_', ' ')}</span>
                                    <button
                                        onClick={() => setStatusFilter('')}
                                        className="hover:bg-blue-200 rounded-full p-0.5"
                                        aria-label="Clear status filter">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {projectFilter && (
                                <div className="flex items-center gap-1 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">
                                    <span>Project: {projectFilter}</span>
                                    <button
                                        onClick={() => {
                                            setProjectFilter('');
                                            setBlockFilter('');
                                        }}
                                        className="hover:bg-indigo-200 rounded-full p-0.5"
                                        aria-label="Clear project filter">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {blockFilter && (
                                <div className="flex items-center gap-1 bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm">
                                    <span>Block: {blockFilter}</span>
                                    <button
                                        onClick={() => setBlockFilter('')}
                                        className="hover:bg-teal-200 rounded-full p-0.5"
                                        aria-label="Clear block filter">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {dateSort && (
                                <div className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                                    <span>Date: {dateSort}</span>
                                    <button
                                        onClick={() => setDateSort('')}
                                        className="hover:bg-green-200 rounded-full p-0.5"
                                        aria-label="Clear date sort">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {pendingSort && (
                                <div className="flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                                    <span>Pending: {pendingSort}</span>
                                    <button
                                        onClick={() => setPendingSort('')}
                                        className="hover:bg-purple-200 rounded-full p-0.5"
                                        aria-label="Clear pending sort">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            {/* Clear All Filters */}
                            {/* <button
                            onClick={() => {
                                setStatusFilter('');
                                setDateSort('');
                                setPendingSort('');
                            }}
                            className="text-xs text-red-600 hover:text-red-800 underline ml-2"
                            aria-label="Clear all filters">
                            Clear All
                        </button> */}
                        </div>
                    )}
                </div>



                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {/* Download Excel Button */}
                    <button
                        type="button"
                        className={`relative flex items-center justify-center self-center shrink-0 ${isDownloadingExcel ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        onClick={handleDownloadExcel}
                        disabled={isDownloadingExcel || !reportData || reportData.length === 0}
                        aria-label="Download Excel"
                        title="Download inventory details as Excel">
                        <img className="w-8 h-8 sm:w-9 sm:h-9" src={FileDownload} alt="Download Excel" />
                    </button>

                    {/* Inventory card: Sold | Unsold */}
                    <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden min-w-[170px] flex-1 basis-[170px]">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-200">
                            <div className="text-[11px] sm:text-xs font-semibold text-gray-700">Inventory</div>
                        </div>
                        <div className="px-2 py-1.5 space-y-0.5">
                            <div className="flex justify-between items-baseline gap-1">
                                <span className="text-[10px] sm:text-xs text-gray-500 font-medium shrink-0">Sold</span>
                                <span
                                    className="text-[10px] sm:text-xs font-bold text-green-600 text-right min-w-0 whitespace-normal break-all leading-tight"
                                    title={`₹${Number(totalSoldPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}>
                                    {formatCurrencyByMagnitude(totalSoldPrice)}
                                </span>
                            </div>
                            <div className="flex justify-between items-baseline gap-1">
                                <span className="text-[10px] sm:text-xs text-gray-500 font-medium shrink-0">Unsold</span>
                                <span
                                    className="text-[10px] sm:text-xs font-bold text-orange-600 text-right min-w-0 whitespace-normal break-all leading-tight"
                                    title={`₹${Number(totalUnsoldPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}>
                                    {formatCurrencyByMagnitude(totalUnsoldPrice)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Plot Area card: Sold | Unsold */}
                    <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden min-w-[170px] flex-1 basis-[170px]">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-200">
                            <div className="text-[11px] sm:text-xs font-semibold text-gray-700">Plot Area</div>
                        </div>
                        <div className="px-2 py-1.5 space-y-0.5">
                            <div className="flex justify-between items-baseline gap-1">
                                <span className="text-[10px] sm:text-xs text-gray-500 font-medium shrink-0">Sold</span>
                                <span className="text-[10px] sm:text-xs font-bold text-green-600 text-right min-w-0 whitespace-normal break-all leading-tight">
                                    {formatArea(totalSoldPlotArea)}{totalSoldPlotArea !== 0 ? ' sq ft' : ''}
                                </span>
                            </div>
                            <div className="flex justify-between items-baseline gap-1">
                                <span className="text-[10px] sm:text-xs text-gray-500 font-medium shrink-0">Unsold</span>
                                <span className="text-[10px] sm:text-xs font-bold text-orange-600 text-right min-w-0 whitespace-normal break-all leading-tight">
                                    {formatArea(totalUnsoldPlotArea)}{totalUnsoldPlotArea !== 0 ? ' sq ft' : ''}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Buildup Area card: Sold | Unsold */}
                    <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden min-w-[170px] flex-1 basis-[170px]">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-200">
                            <div className="text-[11px] sm:text-xs font-semibold text-gray-700">Buildup Area</div>
                        </div>
                        <div className="px-2 py-1.5 space-y-0.5">
                            <div className="flex justify-between items-baseline gap-1">
                                <span className="text-[10px] sm:text-xs text-gray-500 font-medium shrink-0">Sold</span>
                                <span className="text-[10px] sm:text-xs font-bold text-green-600 text-right min-w-0 whitespace-normal break-all leading-tight">
                                    {formatArea(totalSoldBuildUpArea)}{totalSoldBuildUpArea !== 0 ? ' sq ft' : ''}
                                </span>
                            </div>
                            <div className="flex justify-between items-baseline gap-1">
                                <span className="text-[10px] sm:text-xs text-gray-500 font-medium shrink-0">Unsold</span>
                                <span className="text-[10px] sm:text-xs font-bold text-orange-600 text-right min-w-0 whitespace-normal break-all leading-tight">
                                    {formatArea(totalUnsoldBuildUpArea)}{totalUnsoldBuildUpArea !== 0 ? ' sq ft' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {reportData.length > 0 ? (
                <GridWrapper width={100}>
                    <div className="h-[calc(100vh-20rem)] relative flex flex-col">
                        <div className="flex-grow overflow-auto" onClick={closeAllDropdowns}>
                            <table className="min-w-full text-left border-collapse">
                                <thead className="bg-gray-200 text-gray-600 sticky top-0 z-20">
                                    <tr>
                                        <th className={`px-4 py-3 font-semibold whitespace-nowrap relative ${blockFilter ? 'text-blue-700' : ''}`}>
                                            <div
                                                className="flex items-center cursor-pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsBlockHeaderDropdownOpen(!isBlockHeaderDropdownOpen);
                                                    setIsStatusDropdownOpen(false);
                                                    setIsDateDropdownOpen(false);
                                                    setIsPendingDropdownOpen(false);
                                                    setIsProjectDropdownOpen(false);
                                                    setIsBlockDropdownOpen(false);
                                                }}
                                            >
                                                <span>{blockFilter ? `Block: ${blockFilter}` : 'Block Name'}</span>
                                                <ChevronDown size={14} className="ml-1" />
                                            </div>
                                            {isBlockHeaderDropdownOpen && (
                                                <div className="absolute top-full left-0 mt-1 min-w-[12rem] bg-white border border-gray-200 rounded shadow-lg z-50 max-h-[10.5rem] overflow-y-auto">
                                                    <div
                                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${blockFilter === '' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                        onClick={() => {
                                                            setBlockFilter('');
                                                            setIsBlockHeaderDropdownOpen(false);
                                                        }}
                                                    >
                                                        All Blocks
                                                    </div>
                                                    {availableBlocks.map(block => (
                                                        <div
                                                            key={block}
                                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${blockFilter === block ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                            onClick={() => {
                                                                setBlockFilter(block);
                                                                setIsBlockHeaderDropdownOpen(false);
                                                            }}
                                                        >
                                                            {block}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Unit Name</th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap relative">
                                            <div className="flex items-center cursor-pointer" onClick={(e) => {
                                                e.stopPropagation();
                                                setIsStatusDropdownOpen(!isStatusDropdownOpen);
                                                setIsDateDropdownOpen(false);
                                                setIsPendingDropdownOpen(false);
                                                setIsBlockHeaderDropdownOpen(false);
                                                setIsBlockDropdownOpen(false);
                                            }}>
                                                <span>Status</span>
                                                <ChevronDown size={14} className="ml-1" />
                                            </div>
                                            {isStatusDropdownOpen && (
                                                <div className="absolute top-full left-0 mt-1  bg-white border border-gray-200 rounded shadow-lg z-50 max-h-[10.5rem] overflow-y-auto">
                                                    <div
                                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${statusFilter === '' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                        onClick={() => setStatusFilter('')}
                                                    >
                                                        All
                                                    </div>
                                                    {statusOptions.map(option => (
                                                        <div
                                                            key={option}
                                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${statusFilter === option ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                            onClick={() => setStatusFilter(option)}
                                                        >
                                                            {option.replace('_', ' ')}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Customer Name</th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap relative">
                                            <div className="flex items-center cursor-pointer" onClick={(e) => {
                                                e.stopPropagation();
                                                setIsDateDropdownOpen(!isDateDropdownOpen);
                                                setIsStatusDropdownOpen(false);
                                                setIsPendingDropdownOpen(false);
                                                setIsBlockHeaderDropdownOpen(false);
                                                setIsBlockDropdownOpen(false);
                                            }}>
                                                <span>Booking Date</span>
                                                <ArrowUpDown size={14} className="ml-1" />
                                            </div>
                                            {isDateDropdownOpen && (
                                                <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-50">
                                                    <div
                                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${dateSort === '' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                        onClick={() => setDateSort('')}
                                                    >
                                                        Default
                                                    </div>
                                                    {dateSortOptions.map(option => (
                                                        <div
                                                            key={option}
                                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${dateSort === option ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                            onClick={() => setDateSort(option)}
                                                        >
                                                            {option}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                        {accommodationType !== 'Group' && <th className="px-4 py-3 font-semibold whitespace-nowrap">Plot Area</th>}
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Build Area</th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Actual Price</th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Sold Price</th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap">Unsold Price</th>
                                        <th className="px-4 py-3 font-semibold whitespace-nowrap relative">
                                            <div className="flex items-center cursor-pointer" onClick={(e) => {
                                                e.stopPropagation();
                                                setIsPendingDropdownOpen(!isPendingDropdownOpen);
                                                setIsStatusDropdownOpen(false);
                                                setIsDateDropdownOpen(false);
                                                setIsBlockHeaderDropdownOpen(false);
                                                setIsBlockDropdownOpen(false);
                                            }}>
                                                <span>Pending Payments</span>
                                                <ArrowUpDown size={14} className="ml-1" />
                                            </div>
                                            {isPendingDropdownOpen && (
                                                <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-50">
                                                    <div
                                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${pendingSort === '' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                        onClick={() => setPendingSort('')}
                                                    >
                                                        Default
                                                    </div>
                                                    {pendingSortOptions.map(option => (
                                                        <div
                                                            key={option}
                                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm ${pendingSort === option ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                            onClick={() => setPendingSort(option)}
                                                        >
                                                            {option}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {inventoryGroups.length > 0 ? (
                                        inventoryGroups.map((blockGroup) => {
                                            const isBlockOpen = !collapsedBlocks.has(blockGroup.key);
                                            const showFloors = blockHasNamedFloors(blockGroup);
                                            return (
                                                <React.Fragment key={blockGroup.key}>
                                                    <FloorTableGroupHeader
                                                        title={blockGroup.name}
                                                        count={blockGroup.unitCount}
                                                        isOpen={isBlockOpen}
                                                        onToggle={() => toggleBlockCollapse(blockGroup.key)}
                                                        colSpan={columnCount}
                                                        labelPrefix={/^block\b/i.test(String(blockGroup.name || '').trim()) ? '' : 'Block '}
                                                        className="bg-gray-100 border-b border-gray-200"
                                                        buttonClassName="inline-flex items-center gap-2 text-left text-sm font-bold text-gray-900 hover:text-gray-950"
                                                        cellClassName="px-4 py-2.5"
                                                    />
                                                    {isBlockOpen && (showFloors
                                                        ? blockGroup.levels.map((levelGroup) => {
                                                            const isLevelOpen = !collapsedLevels.has(levelGroup.key);
                                                            return (
                                                                <React.Fragment key={levelGroup.key}>
                                                                    <FloorTableGroupHeader
                                                                        title={levelGroup.name}
                                                                        count={levelGroup.rows.length}
                                                                        isOpen={isLevelOpen}
                                                                        onToggle={() => toggleLevelCollapse(levelGroup.key)}
                                                                        colSpan={columnCount}
                                                                        labelPrefix={floorLabelPrefix(levelGroup.name)}
                                                                        className="bg-slate-50 border-b border-gray-200"
                                                                        buttonClassName="inline-flex items-center gap-2 text-left text-sm font-semibold text-gray-700 hover:text-gray-900 pl-4"
                                                                        cellClassName="px-4 py-2"
                                                                    />
                                                                    {isLevelOpen && levelGroup.rows.map((item, index) => renderUnitRow(item, index))}
                                                                </React.Fragment>
                                                            );
                                                        })
                                                        : blockGroup.levels.flatMap((levelGroup) =>
                                                            levelGroup.rows.map((item, index) => renderUnitRow(item, `${blockGroup.key}-${index}`))
                                                        )
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={columnCount} className="text-center py-10 text-gray-500">
                                                No units match the selected filters
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* <div className="mt-4">
                            <PaginationComponent
                                page={page}
                                totalPages={totalPages}
                                onPageChange={(p) => setPage(p)}
                                isFetching={isFetching}
                            />
                        </div> */}
                    </div>
                </GridWrapper>
            ) : isFetching ? (
                <div className="h-[50vh] flex items-center justify-center">
                    <Loader />
                </div>
            ) : (
                <div className="pt-[3rem]">
                    <NoDataFound disabledBtn screenHeight={true} />
                </div>
            )}
        </div>
    );
};

export default SalesInventoryDetails;
